/**
 * MP Materials & Features (PDP)
 * ADDED: 2026-08-03 by MeshPlanet
 *
 * <mp-materials-features> drives the scroll indicator under the mobile track.
 * The bar is a plain progress readout, not a control: it mirrors how far the
 * track has been scrolled, sized to the share of the track that is on screen.
 *
 * Desktop lays the entries out as a grid with nothing to scroll, so the bar is
 * hidden by CSS there. It is also hidden whenever the entries fit without
 * overflowing — a full-width thumb that never moves reads as a broken control.
 *
 * Data attributes:
 *   data-mp-mf-track  the horizontally scrolling list
 *   data-mp-mf-bar    the indicator track (hidden by [hidden] when it can't move)
 *   data-mp-mf-thumb  the filled part, positioned with a translate + width
 */
(function () {
  'use strict';

  class MpMaterialsFeatures extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.track = this.querySelector('[data-mp-mf-track]');
      this.bar = this.querySelector('[data-mp-mf-bar]');
      this.thumb = this.querySelector('[data-mp-mf-thumb]');
      if (!this.track || !this.bar || !this.thumb) return;

      this.update = this.update.bind(this);

      // passive: the handler only reads geometry, so it must never hold up the
      // scroll it is reporting on.
      this.track.addEventListener('scroll', this.update, { passive: true });

      // Catches the breakpoint change and any late layout shift (a lazy image
      // finally landing, the theme editor re-rendering the section around us).
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.track);

      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.update);
      this.observer?.disconnect();
      this.bound = false;
    }

    update() {
      const { scrollWidth, clientWidth, scrollLeft } = this.track;
      const overflow = scrollWidth - clientWidth;

      // Sub-pixel rounding leaves a stray pixel of "overflow" on tracks that
      // actually fit, so anything under a pixel counts as no overflow.
      if (overflow <= 1) {
        this.bar.hidden = true;
        return;
      }
      this.bar.hidden = false;

      const ratio = clientWidth / scrollWidth;

      // scrollLeft is negative in RTL, and past-the-end during an elastic
      // overscroll on iOS — clamped so the thumb stays inside its track.
      const progress = Math.min(Math.max(Math.abs(scrollLeft) / overflow, 0), 1);

      // The thumb travels the part of the bar it does not itself occupy, so the
      // offset is expressed against its own width.
      const travel = (1 - ratio) / ratio;

      this.thumb.style.width = `${ratio * 100}%`;
      this.thumb.style.transform = `translateX(${progress * travel * 100}%)`;
    }
  }

  if (!customElements.get('mp-materials-features')) {
    customElements.define('mp-materials-features', MpMaterialsFeatures);
  }
})();
